import { Phone, Mail, MapPin, MessageCircle, Navigation } from "lucide-react";
import { PageHero } from "../components/site/PageHero";
import { EnquiryForm } from "../components/site/EnquiryForm";
import { Eyebrow, FadeUp } from "../components/site/Reveal";
import { CONTACT } from "../data/services";

const CHANNELS = [
  { icon: Phone, label: "Call", value: CONTACT.phone, href: `tel:${CONTACT.tel}`, id: "phone" },
  { icon: MessageCircle, label: "WhatsApp", value: CONTACT.phone, href: CONTACT.whatsapp, id: "whatsapp" },
  { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}`, id: "email" },
  { icon: MapPin, label: "Office", value: CONTACT.address, href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(CONTACT.mapQuery)}`, id: "location" },
];

export default function Contact() {
  return (
    <>
      <PageHero testid="contact-hero" eyebrow="Contact" lines={["Let's secure", <span className="italic text-[#E3C88A]">what's yours.</span>]}
        intro="Share a few details about your property, land or legal matter. We respond personally and promptly." />
      <section className="paper py-20 sm:py-28" data-testid="contact-body">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-14">
          <div className="lg:col-span-4 space-y-3">
            <Eyebrow>Direct lines</Eyebrow>
            <div className="pt-4 space-y-3">
              {CHANNELS.map((c, i) => {
                const Icon = c.icon;
                const inner = (
                  <>
                    <span className="grid h-12 w-12 shrink-0 place-items-center rounded-full bg-[#0A192F] text-[#E3C88A] transition-transform duration-500 group-hover:rotate-12"><Icon className="h-5 w-5" /></span>
                    <span className="min-w-0">
                      <span className="block font-mono text-[11px] uppercase tracking-[0.22em] text-[#5A6578]">{c.label}</span>
                      <span className={`block mt-1 text-[#0A192F] ${c.id === "email" ? "break-all" : ""}`}>{c.value}</span>
                    </span>
                  </>
                );
                return (
                  <FadeUp key={c.id} delay={i * 0.06}>
                    {c.href ? (
                      <a href={c.href} target={c.id === "whatsapp" || c.id === "location" ? "_blank" : undefined} rel="noopener noreferrer" data-testid={`contact-${c.id}-link`}
                        className="group flex items-center gap-4 rounded-2xl border border-[#0A192F]/12 bg-white p-5 transition-[border-color,transform] duration-300 hover:border-[#8A6A2B] hover:-translate-y-0.5">{inner}</a>
                    ) : (
                      <div className="group flex items-center gap-4 rounded-2xl border border-[#0A192F]/12 bg-white p-5" data-testid={`contact-${c.id}`}>{inner}</div>
                    )}
                  </FadeUp>
                );
              })}
            </div>
          </div>
          <div className="lg:col-span-8 rounded-[28px] bg-white border border-[#0A192F]/10 p-7 sm:p-12">
            <h2 className="font-display text-4xl sm:text-5xl text-[#0A192F] mb-10">Send an enquiry</h2>
            <EnquiryForm />
          </div>
        </div>
      </section>
      <section className="paper pb-20 sm:pb-28" data-testid="contact-map-section">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8 lg:px-12">
          <FadeUp className="grid lg:grid-cols-12 gap-8 items-end mb-8">
            <div className="lg:col-span-7">
              <Eyebrow>Visit the office</Eyebrow>
              <h2 className="mt-5 font-display text-4xl sm:text-5xl text-[#0A192F] leading-tight">Shyamalanagar, <span className="italic text-[#8A6A2B]">Guntur.</span></h2>
            </div>
            <div className="lg:col-span-5 lg:text-right">
              <p className="text-[#2C3545]" data-testid="contact-address">{CONTACT.address}</p>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(CONTACT.mapQuery)}`} target="_blank" rel="noopener noreferrer" data-testid="contact-directions-link"
                className="mt-4 inline-flex items-center gap-2 rounded-full bg-[#0A192F] px-6 py-3 text-sm text-[#F4EEE3] transition-[background-color] duration-300 hover:bg-[#13284A]">
                <Navigation className="h-4 w-4 text-[#E3C88A]" /> Get directions
              </a>
            </div>
          </FadeUp>
          <FadeUp className="relative overflow-hidden rounded-[28px] border border-[#0A192F]/15 h-[360px] sm:h-[460px]">
            <iframe title="Ridhanya Associates office location" data-testid="contact-map-iframe"
              src={`https://maps.google.com/maps?q=${encodeURIComponent(CONTACT.mapQuery)}&z=15&output=embed`}
              className="absolute inset-0 h-full w-full grayscale-[35%] contrast-[1.05]" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </FadeUp>
        </div>
      </section>
    </>
  );
}
